import React from 'react';
import { Ship, Phone, Mail } from 'lucide-react';
import logoImg from '../../assets/logo-cargoags-sm.png';

const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const ViberIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.398.002C9.473.028 5.331.344 3.014 2.467 1.294 4.182.518 6.793.377 10.073c-.141 3.28-.141 9.403 5.208 10.86l.007.008v2.508s-.04.997.621 1.2c.681.209 1.012-.442 1.62-1.12l1.162-1.342c3.2.27 5.666-.34 5.946-.433.647-.211 4.301-.681 4.9-5.55.62-5.029-.295-8.213-1.945-9.671l-.003-.006c-.548-.49-2.705-2.075-7.742-2.12l-.011-.002c-.006 0-.012.002-.018.002-.012 0-.024-.003-.036-.003-.318-.003-.643-.005-.975-.003h.287z"/>
  </svg>
);

const PhoneRow = ({ display, tel }) => (
  <li className="flex items-center gap-2 flex-wrap">
    <a href={`tel:${tel}`} className="hover:text-white transition-colors flex items-center gap-1.5">
      <Phone className="w-3.5 h-3.5" /> {display}
    </a>
    <a
      href={`https://t.me/${tel}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 px-2 py-1 bg-[#229ED9]/15 border border-[#229ED9]/40 text-[#4db8e8] rounded-lg text-xs font-medium hover:bg-[#229ED9] hover:text-white transition-all hover:shadow-[0_0_12px_rgba(34,158,217,0.5)]"
      data-testid={`footer-telegram-${tel}`}
      title="Написати в Telegram"
    >
      <TelegramIcon className="w-3.5 h-3.5" /> Telegram
    </a>
    <a
      href={`viber://chat?number=%2B${tel.replace('+', '')}`}
      className="flex items-center gap-1 px-2 py-1 bg-[#7360f2]/15 border border-[#7360f2]/40 text-[#a99bf5] rounded-lg text-xs font-medium hover:bg-[#7360f2] hover:text-white transition-all hover:shadow-[0_0_12px_rgba(115,96,242,0.5)]"
      data-testid={`footer-viber-${tel}`}
      title="Написати у Viber"
    >
      <ViberIcon className="w-3.5 h-3.5" /> Viber
    </a>
  </li>
);

export const Footer = () => (
  <footer id="contact" className="bg-slate-900 text-white py-12 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10">
              <img src={logoImg} alt="CargoAgs" className="w-10 h-10" data-testid="footer-logo" />
            </div>
            <span className="text-xl font-bold">CargoAgs</span>
          </div>
          <p className="text-slate-400 mb-4 max-w-md">
            Надійна доставка товарів з Китаю в Україну. 
            Працюємо з 2019 року. Понад 50 000 успішних доставок.
          </p>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Послуги</h4>
          <ul className="space-y-2 text-slate-400">
            <li><a href="/dostavka-zbirnyh-vantazhiv" className="hover:text-white transition-colors">Збірні вантажі з Китаю</a></li>
            <li><a href="/vykup-tovariv-z-kytayu" className="hover:text-white transition-colors">Викуп товарів (1688, Taobao)</a></li>
            <li><a href="/oplata-postachalnykam-u-kytai" className="hover:text-white transition-colors">Оплата постачальникам у Китай</a></li>
            <li><a href="/poshuk-postachalnykiv-v-kytai" className="hover:text-white transition-colors">Пошук постачальників (B2B)</a></li>
            <li><a href="/usa" className="hover:text-white transition-colors">Доставка з США</a></li>
            <li><a href="/pravyla-perevezen" className="hover:text-white transition-colors">Правила перевезень</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Контакти</h4>
          <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="footer-online-notice">
            <span className="relative flex h-2.5 w-2.5 mt-1 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400"></span>
            </span>
            <p className="text-amber-200/90 text-sm leading-snug">
              Наразі, зважаючи на ситуацію в країні, ми працюємо <strong className="text-amber-300">онлайн</strong>.
              Приймаємо замовлення та надаємо консультації дистанційно — телефоном, у Telegram, Viber та на пошту.
            </p>
          </div>
          <ul className="space-y-3 text-slate-400">
            <PhoneRow display="+380 63 59 09 619" tel="+380635909619" />
            <PhoneRow display="+380 97 516 06 99" tel="+380975160699" />
            <li>
              <a href="mailto:admin@cargoags.com" className="hover:text-white transition-colors flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> admin@cargoags.com
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500">
        <p>&copy; 2024 CargoAgs. Всі права захищені.</p>
      </div>
    </div>
  </footer>
);

export const FloatingButtons = () => (
  <div className="fixed right-4 bottom-24 z-50 flex flex-col gap-2">
    <a
      href="https://t.me/+380635909619"
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center w-12 h-12 bg-[#229ED9] hover:bg-[#1c8bbf] text-white rounded-full shadow-lg hover:shadow-[0_0_15px_rgba(34,158,217,0.5)] transition-all hover:scale-110"
      data-testid="telegram-btn"
      title="Telegram"
    >
      <TelegramIcon className="w-6 h-6" />
    </a>
    <a 
      href="viber://chat?number=%2B380635909619" 
      className="flex items-center justify-center w-12 h-12 bg-[#7360f2] hover:bg-[#5f4ed6] text-white rounded-full shadow-lg hover:shadow-[0_0_15px_rgba(115,96,242,0.5)] transition-all hover:scale-110"
      data-testid="viber-btn"
      title="Viber"
    >
      <ViberIcon className="w-6 h-6" />
    </a>
  </div>
);
